import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LogIn, Menu, X, ChevronDown } from "lucide-react";
import { Brand } from "../Brand";
import { SECTION_ANCHORS, SECTION_LABELS } from "./useSiteNav";

export const scrollToAnchor = (anchor) => {
  if (anchor === "home") {
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.history.replaceState(null, "", "/");
    return;
  }
  const el = document.getElementById(anchor);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  window.history.replaceState(null, "", `#${anchor}`);
};

export const buildNavItems = (pages, sections) => [
  { id: "home", label: "Home", anchor: "home", to: "/" },
  ...sections.map((k) => ({ id: k, label: SECTION_LABELS[k], anchor: SECTION_ANCHORS[k], to: `/#${SECTION_ANCHORS[k]}` })),
  ...pages.map((p) => ({
    id: `p-${p.slug}`, label: p.title, to: `/p/${p.slug}`,
    children: (p.children || []).map((c) => ({ id: `p-${c.slug}`, label: c.title, to: `/p/${c.slug}` })),
  })),
  { id: "esg-pmi", label: "ESG PMI", href: "https://www.piusostenibili.it" },
];

// Link reale (<a href>): esterno, ancora della home (scroll morbido) o rotta interna.
export const NavLink = ({ item, onHome, onNavigate, className, testid, children }) => {
  if (item.href) {
    return <a href={item.href} target="_blank" rel="noopener noreferrer" data-testid={testid} className={className} onClick={onNavigate}>{children}</a>;
  }
  if (item.anchor && onHome) {
    const href = item.anchor === "home" ? "/" : `#${item.anchor}`;
    return (
      <a href={href} data-testid={testid} className={className}
        onClick={(e) => { e.preventDefault(); scrollToAnchor(item.anchor); onNavigate?.(); }}>{children}</a>
    );
  }
  return <Link to={item.to} data-testid={testid} className={className} onClick={onNavigate}>{children}</Link>;
};

const DESK = "px-3 py-2 text-center text-[13.5px] font-semibold text-gray-300 hover:text-[#ff7e00] transition-colors";

const DesktopNav = ({ items, onHome }) => (
  <nav className="hidden md:flex items-center gap-1" data-testid="landing-nav" aria-label="Menu principale">
    {items.map((n) => (n.children && n.children.length > 0 ? (
      <div key={n.id} className="relative group" data-testid={`nav-group-${n.id}`}>
        <NavLink item={n} onHome={onHome} testid={`nav-${n.id}`} className={`inline-flex items-center gap-1 ${DESK}`}>
          {n.label} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform" />
        </NavLink>
        <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible transition-all duration-150 z-50" data-testid={`nav-dropdown-${n.id}`}>
          <div className="min-w-[210px] rounded-xl bg-[#12233d] border border-white/10 shadow-2xl py-2">
            {n.children.map((c) => (
              <NavLink key={c.id} item={c} onHome={onHome} testid={`nav-${c.id}`}
                className="block w-full text-left px-4 py-2 text-[13px] font-semibold text-gray-300 hover:text-[#ff7e00] hover:bg-white/5 transition-colors">
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    ) : (
      <NavLink key={n.id} item={n} onHome={onHome} testid={`nav-${n.id}`} className={DESK}>{n.label}</NavLink>
    )))}
  </nav>
);

const MobileNav = ({ items, onHome, close }) => (
  <nav className="md:hidden border-t border-white/10 bg-[#0d1b2f] px-6 py-3 max-h-[75vh] overflow-y-auto" data-testid="landing-nav-mobile" aria-label="Menu principale mobile">
    {items.map((n) => (
      <React.Fragment key={n.id}>
        <NavLink item={n} onHome={onHome} onNavigate={close} testid={`nav-m-${n.id}`}
          className="block w-full text-left py-2.5 text-[15px] font-semibold text-gray-200 hover:text-[#ff7e00]">{n.label}</NavLink>
        {(n.children || []).map((c) => (
          <NavLink key={c.id} item={c} onHome={onHome} onNavigate={close} testid={`nav-m-${c.id}`}
            className="block w-full text-left py-2 pl-4 text-[14px] font-medium text-gray-400 hover:text-[#ff7e00]">— {c.label}</NavLink>
        ))}
      </React.Fragment>
    ))}
    <Link to="/seleziona-societa" onClick={close} data-testid="nav-m-login" className="mt-2 inline-flex items-center gap-2 text-[14px] font-bold text-[#ff7e00]"><LogIn size={15} /> Accedi</Link>
  </nav>
);

// Header del sito pubblico (home, /p/*): logo → home, menu con sottomenu, Accedi, menu mobile.
export const SiteHeader = ({ pages = [], sections = [], onHome = false }) => {
  const [open, setOpen] = useState(false);
  const items = buildNavItems(pages, sections);
  const close = () => setOpen(false);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0d1b2f]/85 border-b border-white/10" data-testid="site-header">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <NavLink item={{ anchor: "home", to: "/" }} onHome={onHome} testid="site-brand" className="shrink-0">
          <Brand dark className="text-[24px]" />
        </NavLink>
        <DesktopNav items={items} onHome={onHome} />
        <div className="flex items-center gap-2">
          <Link to="/seleziona-societa" data-testid="landing-login"
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-[#ff7e00] hover:text-[#ff7e00] px-4 py-2 text-[13px] font-bold transition-colors">
            <LogIn size={15} /> Accedi
          </Link>
          <button type="button" onClick={() => setOpen((v) => !v)} data-testid="landing-nav-toggle" aria-label="Apri menu" aria-expanded={open} className="md:hidden text-white p-2">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && <MobileNav items={items} onHome={onHome} close={close} />}
    </header>
  );
};

export default SiteHeader;
